import React, { useMemo, useState } from 'react';
import { ChevronUp } from 'lucide-react';
interface ExpandableHiringFunnelProps {
  estimatedHires: number;
}
type StageId = 'hires' | 'offers' | 'finalInterviews' | 'finalsScheduled' | 'qualified' | 'screeningInterviews' | 'reachOuts' | 'approved' | 'applications';
interface ConversionRates {
  offerAcceptance: number;
  finalOfferRate: number;
  finalShowRate: number;
  qualifiedRate: number;
  screeningPassRate: number;
  reachOutConversionRate: number;
  reachOutMultiplier: number;
  approvedQualificationRate: number;
}
interface StageConfig {
  id: StageId;
  label: string;
  color: string;
  rateKey?: keyof ConversionRates;
  rateLabel?: string;
}
export function ExpandableHiringFunnel({
  estimatedHires
}: ExpandableHiringFunnelProps) {
  // Track the index of the highest visible stage (0 = Hires only)
  const [visibleIndex, setVisibleIndex] = useState(0);
  // Editable conversion rates - updated defaults based on realistic recruiting metrics
  const [rates, setRates] = useState<ConversionRates>({
    offerAcceptance: 80,
    finalOfferRate: 60,
    finalShowRate: 50,
    qualifiedRate: 50,
    screeningPassRate: 70,
    reachOutConversionRate: 75,
    reachOutMultiplier: 250,
    approvedQualificationRate: 35
  });
  // Track which rate is being edited
  const [editingRate, setEditingRate] = useState<keyof ConversionRates | null>(null);
  const [editValue, setEditValue] = useState('');
  // Define stages in order from bottom to top (Hired -> Applications)
  const stagesConfig: StageConfig[] = useMemo(() => [{
    id: 'hires',
    label: 'Hired',
    color: 'from-navy to-navy/90'
  }, {
    id: 'offers',
    label: 'Offered',
    color: 'from-amber-500 to-amber-400',
    rateKey: 'offerAcceptance',
    rateLabel: 'acceptance rate'
  }, {
    id: 'finalInterviews',
    label: 'Interviewed',
    color: 'from-purple-400 to-purple-300',
    rateKey: 'finalOfferRate',
    rateLabel: 'offer rate'
  }, {
    id: 'finalsScheduled',
    label: 'Scheduled',
    color: 'from-indigo-500 to-indigo-400',
    rateKey: 'finalShowRate',
    rateLabel: 'show rate'
  }, {
    id: 'qualified',
    label: 'Qualified',
    color: 'from-indigo-400 to-indigo-300',
    rateKey: 'qualifiedRate',
    rateLabel: 'conversion rate'
  }, {
    id: 'screeningInterviews',
    label: 'Screened',
    color: 'from-blue-500 to-blue-400',
    rateKey: 'screeningPassRate',
    rateLabel: 'qualified rate'
  }, {
    id: 'reachOuts',
    label: 'Approved Reach Outs',
    color: 'from-gray-500 to-gray-400',
    rateKey: 'reachOutMultiplier',
    rateLabel: 'avg attempts'
  }, {
    id: 'approved',
    label: 'Approved',
    color: 'from-gray-400 to-gray-300',
    rateKey: 'reachOutConversionRate',
    rateLabel: 'contact rate'
  }, {
    id: 'applications',
    label: 'Applications',
    color: 'from-gray-300 to-gray-200',
    rateKey: 'approvedQualificationRate',
    rateLabel: 'approval rate'
  }], []);
  // Calculate values for all stages based on current rates
  const stageValues = useMemo(() => {
    const h = estimatedHires;
    const r = rates;
    return stagesConfig.map(stage => {
      let value: number;
      switch (stage.id) {
        case 'hires':
          value = h;
          break;
        case 'offers':
          value = h / (r.offerAcceptance / 100);
          break;
        case 'finalInterviews':
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100);
          break;
        case 'finalsScheduled':
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100);
          break;
        case 'qualified':
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100) / (r.qualifiedRate / 100);
          break;
        case 'screeningInterviews':
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100) / (r.qualifiedRate / 100) / (r.screeningPassRate / 100);
          break;
        case 'approved':
          // Good Fit = Screening Convos / contact rate
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100) / (r.qualifiedRate / 100) / (r.screeningPassRate / 100) / (r.reachOutConversionRate / 100);
          break;
        case 'reachOuts':
          // Good Fit Reach Outs = Good Fit × multiplier
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100) / (r.qualifiedRate / 100) / (r.screeningPassRate / 100) / (r.reachOutConversionRate / 100) * (r.reachOutMultiplier / 100);
          break;
        case 'applications':
          // Applications = Good Fit / approved rate
          value = h / (r.offerAcceptance / 100) / (r.finalOfferRate / 100) / (r.finalShowRate / 100) / (r.qualifiedRate / 100) / (r.screeningPassRate / 100) / (r.reachOutConversionRate / 100) / (r.approvedQualificationRate / 100);
          break;
        default:
          value = 0;
      }
      return {
        ...stage,
        value: Math.round(value)
      };
    });
  }, [estimatedHires, rates, stagesConfig]);
  // Get currently visible stages (reversed for display: Top -> Bottom)
  const visibleStages = useMemo(() => {
    return stageValues.slice(0, visibleIndex + 1).reverse();
  }, [stageValues, visibleIndex]);
  // Use Reach Outs as the 100% width reference (when visible)
  const maxValue = useMemo(() => {
    const reachOutsStage = visibleStages.find(s => s.id === 'reachOuts');
    return reachOutsStage ? reachOutsStage.value : visibleStages[0].value;
  }, [visibleStages]);
  // Check if Applications is visible (index 8 in stagesConfig)
  const isApplicationsVisible = visibleIndex >= 8;
  // Get Applications and Hires values for percentage calculation
  const applicationsValue = useMemo(() => {
    return stageValues.find(s => s.id === 'applications')?.value || 0;
  }, [stageValues]);
  const hiresValue = useMemo(() => {
    return stageValues.find(s => s.id === 'hires')?.value || 0;
  }, [stageValues]);
  const hireRatePercent = useMemo(() => {
    if (!isApplicationsVisible || applicationsValue === 0) return null;
    return (hiresValue / applicationsValue * 100).toFixed(1);
  }, [isApplicationsVisible, hiresValue, applicationsValue]);
  const handleExpand = () => {
    if (visibleIndex < stagesConfig.length - 1) {
      setVisibleIndex(prev => prev + 1);
    }
  };
  const startEditingRate = (rateKey: keyof ConversionRates) => {
    setEditingRate(rateKey);
    setEditValue(rates[rateKey].toString());
  };
  const saveRateEdit = () => {
    if (editingRate) {
      const value = parseFloat(editValue);
      if (!isNaN(value) && value > 0) {
        setRates(prev => ({
          ...prev,
          [editingRate]: value
        }));
      }
      setEditingRate(null);
      setEditValue('');
    }
  };
  const cancelRateEdit = () => {
    setEditingRate(null);
    setEditValue('');
  };
  const handleRateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveRateEdit();
    } else if (e.key === 'Escape') {
      cancelRateEdit();
    }
  };
  const canExpand = visibleIndex < stagesConfig.length - 1;
  return <div className="w-full space-y-4" data-id="element-228" data-mp="1uhvrrz93n4kj">
      {visibleStages.map((stage, index) => {
      const isTop = index === 0;
      const widthPercent = stage.value / maxValue * 100;
      const rateKey = stage.rateKey;
      const currentRate = rateKey ? rates[rateKey] : null;
      const isEditingThisRate = rateKey && editingRate === rateKey;
      return <div key={stage.id} className="relative group" data-id="element-229" data-mp="wvgbh0sescyk" data-mpi={index}>
            {/* Expand Arrow - Only for top stage and if can expand */}
            {isTop && canExpand && <button onClick={handleExpand} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Expand funnel" data-id="element-230" data-mp="16sb6vatdxx8m">
                <div className="w-20 text-right flex justify-end pr-4" data-id="element-231" data-mp="e4fe2h6xsph4">
                  <ChevronUp className="w-5 h-5 text-amber-500 animate-bounce" data-id="element-232" data-mp="3opp0ra1ip7d7" />
                </div>
                <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-233" data-mp="3tojpesm61q58">
                  Click to expand
                </div>
              </button>}

            <div className="flex items-center gap-3" data-id="element-234" data-mp="2kla7kq1bjw8">
              {/* Value */}
              <div className="w-20 text-right flex-shrink-0" data-id="element-235" data-mp="6lnbs87fc81d">
                <span className={`text-xl font-bold ${stage.id === 'hires' ? 'text-navy' : 'text-gray-700'}`} data-id="element-236" data-mp="bjymel5jmmkn">
                  {stage.value.toLocaleString()}
                </span>
              </div>

              {/* Label */}
              <div className="w-32 md:w-48 text-base font-semibold text-gray-700 flex-shrink-0 truncate" title={stage.label} data-id="element-237" data-mp="l9i6tv8wmohm">
                {stage.label}
              </div>

              {/* Bar - Hidden for Approved Reach Outs */}
              {stage.id !== 'reachOuts' && <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden" data-id="element-238" data-mp="z8800zsls40p">
                  <div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${stage.color} rounded-full transition-all duration-500 ease-out`} style={{
              width: `${widthPercent}%`
            }} data-id="element-239" data-mp="2jrbkvey79tdo" />
                </div>}
              {stage.id === 'reachOuts' && <div className="flex-1" data-id="element-240" data-mp="2as826c2y3fzp"></div>}

              {/* Percentage / Conversion Rate - Two Lines */}
              <div className="w-20 md:w-28 text-right flex-shrink-0 hidden sm:block" data-id="element-241" data-mp="25tdk5ccsxvgn">
                {stage.id === 'hires' ?
            // Show "of applications" label when Applications is visible
            hireRatePercent !== null ? <div data-id="element-242" data-mp="2tog8lj7p1wwl">
                      <div className="text-base font-bold text-gray-700" data-id="element-243" data-mp="2tny6i5ycoogr">
                        {hireRatePercent}%
                      </div>
                      <div className="text-xs text-gray-500 leading-tight" data-id="element-244" data-mp="2oo29iqo2lxap">
                        of applications
                      </div>
                    </div> : null : rateKey && currentRate !== null ? isEditingThisRate ? <input type="number" min="0" step="1" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleRateKeyDown} onBlur={saveRateEdit} autoFocus className="w-full text-center border-2 border-amber rounded p-1 text-sm font-bold focus:border-amber outline-none bg-white" data-id="element-245" data-mp="3jhssxq4xlq6i" /> : <div onClick={() => startEditingRate(rateKey)} className="cursor-pointer hover:text-amber-600 transition-colors" data-id="element-246" data-mp="3sg5vhkizhjrv">
                      <div className="text-base font-bold text-gray-700" data-id="element-247" data-mp="6ailga27czur">
                        {rateKey === 'reachOutMultiplier' ? `${(currentRate / 100).toFixed(1)}x` : `${currentRate}%`}
                      </div>
                      <div className="text-xs text-gray-500 leading-tight" data-id="element-248" data-mp="fzsh1zw990nm">
                        {stage.rateLabel}
                      </div>
                    </div> : null}
              </div>
            </div>
          </div>;
    })}
    </div>;
}